import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, Play, Plus, RefreshCw, Trash2 } from "lucide-react";
import { parameterSets as paramSetsApi, runs as runsApi } from "@/api/endpoints";
import { serverDetail } from "@/api/client";
import StatusPill from "@/components/StatusPill";
import SpinnerButton from "@/components/SpinnerButton";
import ConfirmDialog from "@/components/ConfirmDialog";
import EmptyState from "@/components/EmptyState";
import { relativeTime, shortId } from "@/lib/format";

const RUN_TYPES = ["monte_carlo", "sweep", "guidance_compare"];

export default function ParameterSetsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [runDialog, setRunDialog] = useState(null);
  const [runType, setRunType] = useState("monte_carlo");
  const [submitting, setSubmitting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const data = await paramSetsApi.list({ limit: 200 });
      setItems(data);
    } catch (e) {
      setErr(serverDetail(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function doDelete() {
    if (!confirmDel) return;
    setDeleting(true);
    try {
      await paramSetsApi.remove(confirmDel.id);
      setConfirmDel(null);
      await refresh();
    } catch (e) {
      setErr(serverDetail(e));
    } finally {
      setDeleting(false);
    }
  }

  function askRun(ps) {
    setRunType(ps.run_type || "monte_carlo");
    setRunDialog(ps);
  }

  async function doRun() {
    if (!runDialog) return;
    setSubmitting(true);
    try {
      const r = await runsApi.submit({
        parameter_set_id: runDialog.id,
        run_type: runType,
      });
      setRunDialog(null);
      navigate(`/runs/${r.id}`);
    } catch (e) {
      setErr(serverDetail(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-slate-900" data-testid="param-sets-title">
          Parameter Sets
        </h1>
        <div className="flex items-center gap-2">
          <SpinnerButton
            variant="secondary"
            loading={loading}
            onClick={refresh}
            testId="param-sets-refresh"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </SpinnerButton>
          {items.length > 0 && (
            <SpinnerButton
              onClick={() => navigate("/plan/scenarios")}
              testId="param-sets-new-button"
            >
              Open Scenarios
            </SpinnerButton>
          )}
        </div>
      </div>

      {err && (
        <div className="mb-3 text-sm text-red-800 bg-red-50 border border-red-200 rounded-md p-2">
          {err}
        </div>
      )}

      {!loading && items.length === 0 ? (
        <EmptyState
          testId="param-sets-empty"
          title="No parameter sets yet"
          subtitle="Parameter sets were the legacy one-click re-run surface. The Scenarios library + Workbench Run button now cover that flow."
          actionLabel="Open Scenarios"
          onAction={() => navigate("/plan/scenarios")}
          actionTestId="param-sets-empty-cta"
        />
      ) : (
        <div className="overflow-x-auto bg-white border border-slate-200 rounded-md">
        <table className="min-w-[640px] w-full text-sm" data-testid="param-sets-table">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
            <tr>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Run type</th>
              <th className="hidden md:table-cell text-left px-3 py-2">Tags</th>
              <th className="hidden md:table-cell text-left px-3 py-2">Created</th>
              <th className="hidden lg:table-cell text-left px-3 py-2">Updated</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center text-slate-400 py-10">
                  {loading ? "Loading…" : "No parameter sets yet."}
                </td>
              </tr>
            )}
            {items.map((p) => (
              <tr
                key={p.id}
                className="border-t border-slate-100 hover:bg-slate-50/50 h-12"
                data-testid={`param-sets-row-${p.id}`}
              >
                <td className="px-3 py-2">
                  <Link
                    to={`/parameter-sets/${p.id}`}
                    className="text-slate-900 hover:underline font-medium"
                  >
                    {p.name}
                  </Link>
                  {p.is_seed && (
                    <span className="ml-2">
                      <StatusPill status="ok">seed</StatusPill>
                    </span>
                  )}
                  <div className="text-xs text-slate-400 font-mono">{shortId(p.id)}</div>
                </td>
                <td className="px-3 py-2 font-mono text-xs">{p.run_type}</td>
                <td className="hidden md:table-cell px-3 py-2">
                  <div className="flex gap-1 flex-wrap">
                    {(p.tags || []).map((t) => (
                      <span
                        key={t}
                        className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="hidden md:table-cell px-3 py-2 text-slate-600">{relativeTime(p.created_at)}</td>
                <td className="hidden lg:table-cell px-3 py-2 text-slate-600">{relativeTime(p.updated_at)}</td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex gap-1">
                    <Link
                      to={`/parameter-sets/${p.id}`}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md text-slate-700 hover:bg-slate-100"
                      data-testid={`param-sets-view-${p.id}`}
                    >
                      <Eye className="w-3.5 h-3.5" /> View
                    </Link>
                    <button
                      type="button"
                      onClick={() => askRun(p)}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md text-slate-700 hover:bg-slate-100"
                      data-testid={`param-sets-run-${p.id}`}
                    >
                      <Play className="w-3.5 h-3.5" /> Run
                    </button>
                    <button
                      type="button"
                      disabled={p.is_seed}
                      onClick={() => setConfirmDel(p)}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md text-red-700 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid={`param-sets-delete-${p.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      <ConfirmDialog
        open={!!confirmDel}
        title="Delete parameter set?"
        message={
          confirmDel
            ? `This will permanently delete "${confirmDel.name}".`
            : ""
        }
        confirmLabel="Delete"
        loading={deleting}
        onCancel={() => setConfirmDel(null)}
        onConfirm={doDelete}
      />

      {runDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5">
            <h3 className="text-base font-semibold mb-2">Run parameter set</h3>
            <p className="text-sm text-slate-600 mb-3">
              Submit a new run from{" "}
              <span className="font-mono">{runDialog.name}</span>.
            </p>
            <label className="block">
              <span className="text-xs font-medium text-slate-700">Run type</span>
              <select
                value={runType}
                onChange={(e) => setRunType(e.target.value)}
                className="mt-1 w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"
                data-testid="param-sets-run-type-select"
              >
                {RUN_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <SpinnerButton variant="secondary" onClick={() => setRunDialog(null)}>
                Cancel
              </SpinnerButton>
              <SpinnerButton
                loading={submitting}
                onClick={doRun}
                testId="param-sets-run-confirm"
              >
                Submit run
              </SpinnerButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
